/**
 * Where each body region sits on the printed figure.
 *
 * This exists because the first attempt placed labels in two columns by array index, which put
 * "Arm links" beside the hips and "Bein rechts" beside the feet. On a card that a person points to
 * when they are in pain — and that a paramedic may read — a mislabelled body is worse than no card
 * at all. So each region is a **shape drawn in its own place**, with a number inside it and the word
 * in a legend. Numbers cannot drift out of position the way floating labels can.
 *
 * It lives here, not in `notfallData`, because it is paper presentation. The region names and the
 * sentences stay the app's; only the drawing is local.
 *
 * **Left and right are the person's own**, which on a front view means the viewer's opposite side —
 * the convention every clinical body chart uses. On the back view they coincide.
 */

export interface PrintShape {
  /** Rounded rectangle in the 100 × 210 viewBox. */
  x: number;
  y: number;
  width: number;
  height: number;
  radius?: number;
}

/** Region id → its place on the figure. Every region in `BODY_REGIONS` must appear here. */
export const BODY_PRINT_SHAPES: Record<string, PrintShape> = {
  // ── Front, viewed from in front: the person's left is on the viewer's right ──
  kopf: { x: 37, y: 3, width: 26, height: 26, radius: 13 },
  hals: { x: 42, y: 30, width: 16, height: 6, radius: 3 },
  brust: { x: 30, y: 37, width: 40, height: 25, radius: 5 },
  bauch: { x: 30, y: 63, width: 40, height: 25, radius: 5 },
  huefte: { x: 30, y: 89, width: 40, height: 16, radius: 5 },
  'arm-links': { x: 72, y: 37, width: 16, height: 56, radius: 8 },
  'arm-rechts': { x: 12, y: 37, width: 16, height: 56, radius: 8 },
  'bein-links': { x: 51, y: 106, width: 18, height: 88, radius: 8 },
  'bein-rechts': { x: 31, y: 106, width: 18, height: 88, radius: 8 },

  // ── Back, viewed from behind: left and right coincide with the viewer's ──
  hinterkopf: { x: 37, y: 3, width: 26, height: 26, radius: 13 },
  nacken: { x: 42, y: 30, width: 16, height: 6, radius: 3 },
  schultern: { x: 26, y: 37, width: 48, height: 14, radius: 5 },
  'ruecken-oben': { x: 30, y: 52, width: 40, height: 22, radius: 5 },
  'ruecken-unten': { x: 30, y: 75, width: 40, height: 20, radius: 5 },
  gesaess: { x: 30, y: 96, width: 40, height: 16, radius: 5 },
  'bein-hinten-links': { x: 31, y: 113, width: 18, height: 81, radius: 8 },
  'bein-hinten-rechts': { x: 51, y: 113, width: 18, height: 81, radius: 8 },
};
